import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "notistack";
import {
  useDownloadReportMutation,
  useReportsQuery,
} from "@/partner/entities/reports";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { useIsMobile } from "@/shared/hooks";
import {
  type ReportsListQueryType,
  ReportStatusType,
} from "@/types/graphql/partner.generated";
import { reportErrorKey, reportTitleKey } from "../model/reportLabels";

type ReportRow = ReportsListQueryType["reports"]["nodes"][number];

const POLL_INTERVAL_MS = 5000;

const STATUS_COLOR: Record<
  ReportStatusType,
  "default" | "info" | "success" | "error"
> = {
  [ReportStatusType.PendingType]: "default",
  [ReportStatusType.ProcessingType]: "info",
  [ReportStatusType.CompletedType]: "success",
  [ReportStatusType.ErrorType]: "error",
};

const DownloadAction: FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [download, { loading }] = useDownloadReportMutation({
    variables: { id },
    refetchQueries: ["ReportsList"],
    onCompleted: (result) => {
      // Не window.open: после await мутации блокировщик всплывающих окон
      // режет попап. XLSX отдаётся на скачивание, страница остаётся.
      window.location.assign(result.downloadReport.url);
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  return (
    <Tooltip title={t("reports.list.download")}>
      <span>
        <IconButton disabled={loading} onClick={() => download()}>
          <DownloadIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const ReportsList: FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile("md");
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error, startPolling, stopPolling } = useReportsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        type: filter.type || undefined,
        status: filter.status || undefined,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.reports?.nodes ?? [];
  const count = data?.reports?.count ?? 0;

  // Опрос нужен, только пока в списке есть отчёты в очереди или в работе.
  const active = rows.some(
    (row) =>
      row.status === ReportStatusType.PendingType ||
      row.status === ReportStatusType.ProcessingType
  );
  useEffect(() => {
    if (active) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
  }, [active, startPolling, stopPolling]);

  const emptyValue = t("common.emptyDash");

  const formatDate = (value: string): string =>
    interpolationFormat(value, "short", i18n.language);

  const formatPeriod = (row: ReportRow): string => {
    const { dateFrom, dateTo } = (row.filter ?? {}) as {
      dateFrom?: string;
      dateTo?: string;
    };
    return dateFrom && dateTo
      ? `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
      : "";
  };

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("reports.list.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("reports.list.titleColumn")}</TableCell>
            <TableCell>{t("reports.list.filterColumn")}</TableCell>
            <TableCell>{t("reports.list.statusColumn")}</TableCell>
            {!isMobile && (
              <TableCell>{t("reports.list.creatorColumn")}</TableCell>
            )}
            <TableCell>{t("reports.list.createdAtColumn")}</TableCell>
            {!isMobile && (
              <TableCell align="right">
                {t("reports.list.rowsColumn")}
              </TableCell>
            )}
            <TableCell align="right" sx={{ width: 80 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {t(reportTitleKey(row.type, row.widget))}
                </Typography>
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatPeriod(row)}
                </Typography>
              </TableCell>

              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {row.status === ReportStatusType.ErrorType ? (
                    <Tooltip title={t(reportErrorKey(row.error?.code))}>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[row.status]}
                        label={t(`reports.status.${row.status.toLowerCase()}`)}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      color={STATUS_COLOR[row.status]}
                      label={t(`reports.status.${row.status.toLowerCase()}`)}
                    />
                  )}
                  {row.status === ReportStatusType.ProcessingType && (
                    <CircularProgress
                      size={14}
                      variant="determinate"
                      value={row.progress}
                    />
                  )}
                </Box>
              </TableCell>

              {!isMobile && (
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {row.creator?.email ?? emptyValue}
                  </Typography>
                </TableCell>
              )}

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatDate(row.createdAt)}
                </Typography>
              </TableCell>

              {!isMobile && (
                <TableCell align="right">
                  <Typography variant="body2">
                    {row.rowCount ?? emptyValue}
                  </Typography>
                </TableCell>
              )}

              <TableCell align="right">
                {row.status === ReportStatusType.CompletedType && (
                  <DownloadAction id={row.id} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
